import { addMinutes } from "date-fns";
import { FieldValues } from "react-hook-form";
import { PlanningModeEnum } from "shared/enums/globalEnums";
import {
  FcrDConfig,
  FinlandAfrrConfig,
  FinlandAfrrInitialSocRestorationPowerEnum,
  GenericPlanningConfig,
  GenericPlanningValue,
} from "./types";
import { FcrDEngagementFields } from "./initialFields";

function computeFcrDDefaultEngagements(cfg: FcrDConfig, initialData: FieldValues) {
  const [engagementField, engagementFieldName] = cfg.defaultEngagementFieldMapping;

  if (engagementField === FcrDEngagementFields.oneHourTestCapacity) {
    return {
      fcrd_up_engagement: initialData.oneHourTestCapacity,
      fcrd_down_engagement: initialData.oneHourTestCapacity,
    };
  }

  return {
    fcrd_up_engagement: 0,
    fcrd_down_engagement: 0,
    ...(engagementFieldName && {
      [engagementFieldName]: initialData[engagementField],
    }),
  };
}

export function computeFinlandAfrrDefaultEngagements(cfg: FinlandAfrrConfig, initialData: FieldValues) {
  const { engagementUpConst, engagementDownConst, initialSocRestorationPower } = cfg;

  return {
    afrr_engagement_up: engagementUpConst === "default" ? initialData.afrr_capacity : engagementUpConst,
    afrr_engagement_down: engagementDownConst === "default" ? initialData.afrr_capacity : engagementDownConst,
    initialSocRestorationPower:
      initialSocRestorationPower === FinlandAfrrInitialSocRestorationPowerEnum.positive
        ? initialData.baseline_power
        : -initialData.baseline_power,
  };
}

export function computeDefaultValuesGeneric(
  configs: GenericPlanningConfig[],
  initialData: FieldValues,
): Record<string, GenericPlanningValue> {
  return configs.reduce<Record<string, GenericPlanningValue>>((acc, cfg, idx) => {
    const prev = idx > 0 ? acc[configs[idx - 1].name] : null;
    const start_date = idx === 0 ? new Date(initialData.startDate) : (prev?.end_date as Date);

    const end_date = addMinutes(start_date, cfg.durationInMinutes);

    let base: GenericPlanningValue = {
      active: cfg.activeDefault ?? true,
      start_date,
      end_date,
      mode: cfg.mode,
      chronicleFilename: cfg.chronicleFilename ?? null,
    };

    if (cfg.mode === PlanningModeEnum.soc_management) {
      base.targetSoc = cfg.targetSoc;
    } else if (cfg.mode === PlanningModeEnum.fingrid_fcrn) {
      const value =
        cfg.engagementSource === "min" ? initialData.minimalOperationalPower : initialData.maximalOperationalPower;
      base.fcrn_engagement = value ?? null;
    } else if (cfg.mode === PlanningModeEnum.fingrid_fcrd) {
      base = {
        ...base,
        ...computeFcrDDefaultEngagements(cfg, initialData),
      };
    } else if (cfg.mode === PlanningModeEnum.fingrid_afrr) {
      base = {
        ...base,
        ...computeFinlandAfrrDefaultEngagements(cfg, initialData),
      };
    }

    acc[cfg.name] = base;
    return acc;
  }, {});
}
